import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface ProfileData {
  fullName: string
  jobTitle: string
  phone: string
  timezone: string
  avatarUrl?: string
}

export interface OrganizationData {
  companyName: string
  website: string
  industry: string
  companySize: string
  country: string
}

export interface TeamInvite {
  email: string
  role: 'admin' | 'member'
}

export interface AttestationData {
  selectedFrameworks: string[]
  currentFrameworkIndex: number
  frameworkAttestations: Record<
    string,
    {
      acknowledgments: Record<string, boolean>
      signatureType: 'typed' | 'drawn'
      signatureData: string
      representativeName: string
      representativeTitle: string
      representativeEmail: string
      submittedAt: string
    }
  >
}

export interface OnboardingState {
  currentStep: number
  totalSteps: number
  accountType: 'business' | 'developer' | null
  profileData: ProfileData
  organizationData: OrganizationData
  selectedProducts: string[]
  teamInvites: TeamInvite[]
  attestationData: AttestationData | null

  // Actions
  setStep: (step: number) => void
  nextStep: () => void
  prevStep: () => void
  setAccountType: (type: 'business' | 'developer') => void
  setProfileData: (data: Partial<ProfileData>) => void
  setOrganizationData: (data: Partial<OrganizationData>) => void
  setSelectedProducts: (products: string[]) => void
  addTeamInvite: (invite: TeamInvite) => void
  removeTeamInvite: (index: number) => void
  updateTeamInvite: (index: number, invite: Partial<TeamInvite>) => void
  setAttestationData: (data: AttestationData | null) => void
  reset: () => void
  isStepComplete: (step: number) => boolean
}

const initialProfileData: ProfileData = {
  fullName: '',
  jobTitle: '',
  phone: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
  avatarUrl: undefined,
}

const initialOrganizationData: OrganizationData = {
  companyName: '',
  website: '',
  industry: '',
  companySize: '',
  country: '',
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      currentStep: 1,
      totalSteps: 7,
      accountType: null,
      profileData: { ...initialProfileData },
      organizationData: { ...initialOrganizationData },
      selectedProducts: [],
      teamInvites: [],
      attestationData: null,

      setStep: (step: number) => {
        if (step >= 1 && step <= get().totalSteps) {
          set({ currentStep: step })
        }
      },

      nextStep: () => {
        const { currentStep, totalSteps } = get()
        if (currentStep < totalSteps) {
          set({ currentStep: currentStep + 1 })
        }
      },

      prevStep: () => {
        const { currentStep } = get()
        if (currentStep > 1) {
          set({ currentStep: currentStep - 1 })
        }
      },

      setAccountType: (type: 'business' | 'developer') => {
        set({ accountType: type })
      },

      setProfileData: (data: Partial<ProfileData>) => {
        set((state) => ({
          profileData: { ...state.profileData, ...data },
        }))
      },

      setOrganizationData: (data: Partial<OrganizationData>) => {
        set((state) => ({
          organizationData: { ...state.organizationData, ...data },
        }))
      },

      setSelectedProducts: (products: string[]) => {
        set({ selectedProducts: products })
      },

      addTeamInvite: (invite: TeamInvite) => {
        const { teamInvites } = get()
        if (teamInvites.length < 5) {
          set({ teamInvites: [...teamInvites, invite] })
        }
      },

      removeTeamInvite: (index: number) => {
        set((state) => ({
          teamInvites: state.teamInvites.filter((_, i) => i !== index),
        }))
      },

      updateTeamInvite: (index: number, invite: Partial<TeamInvite>) => {
        set((state) => ({
          teamInvites: state.teamInvites.map((item, i) =>
            i === index ? { ...item, ...invite } : item
          ),
        }))
      },

      setAttestationData: (data: AttestationData | null) => {
        set({ attestationData: data })
      },

      reset: () => {
        set({
          currentStep: 1,
          accountType: null,
          profileData: { ...initialProfileData },
          organizationData: { ...initialOrganizationData },
          selectedProducts: [],
          teamInvites: [],
          attestationData: null,
        })
      },

      isStepComplete: (step: number) => {
        const state = get()
        switch (step) {
          case 1: // Welcome
            return true // Always can proceed from welcome
          case 2: // Profile
            return !!(state.profileData.fullName && state.profileData.timezone)
          case 3: // Organization
            return !!(
              state.organizationData.companyName &&
              state.organizationData.industry &&
              state.organizationData.companySize &&
              state.organizationData.country
            )
          case 4: // Products
            return state.selectedProducts.length > 0
          case 5: // Attestation (optional)
            return true
          case 6: // Team (optional)
            return true
          case 7: // Complete
            return true
          default:
            return false
        }
      },
    }),
    {
      name: 'mindmetal-onboarding',
      partialize: (state) => ({
        currentStep: state.currentStep,
        accountType: state.accountType,
        profileData: state.profileData,
        organizationData: state.organizationData,
        selectedProducts: state.selectedProducts,
        teamInvites: state.teamInvites,
        attestationData: state.attestationData,
      }),
    }
  )
)

// Step definitions for the progress stepper
export const ONBOARDING_STEPS = [
  { number: 1, name: 'Welcome', path: 'welcome' },
  { number: 2, name: 'Profile', path: 'profile' },
  { number: 3, name: 'Organization', path: 'organization' },
  { number: 4, name: 'Products', path: 'products' },
  { number: 5, name: 'Compliance', path: 'attestation' },
  { number: 6, name: 'Team', path: 'invite-team' },
  { number: 7, name: 'Complete', path: 'complete' },
] as const
